import { parsePhoneNumber } from 'libphonenumber-js';
import { E164PhoneRegex } from '../../utils/regex';
import { convertMonthsToYears } from './converters';

/**
 * Inosio Date Formatter
 * 07012000 -> 07/01/2000
 * @param date
 * @returns
 */
export const InosioDateFormat = (date: string) => {
  date = date.replace(/\D/g, '');

  if (date.length === 0) {
    return '';
  }

  let formattedDate = '';
  if (date.length >= 8) {
    formattedDate += date.substring(0, 2) + '/';
    formattedDate += date.substring(2, 4) + '/';
    formattedDate += date.substring(4, 8);
  } else if (date.length >= 5) {
    formattedDate += date.substring(0, 2) + '/';
    formattedDate += date.substring(2, 4) + '/';
    formattedDate += date.substring(4);
  } else if (date.length >= 3) {
    formattedDate += date.substring(0, 2) + '/';
    formattedDate += date.substring(2);
  } else {
    formattedDate = date;
  }

  return formattedDate;
};

/**
 * Inosio Phone Number formatter
 * 1234567890 -> 123-456-7890
 * @param number
 * @returns
 */
export const InosioPhoneFormat = (number: string) => {
  const phoneNumber = number.replace(/\D/g, '');

  if (phoneNumber.length === 0) {
    return '';
  }

  let formattedPhoneNumber = '';
  if (phoneNumber.length >= 10) {
    formattedPhoneNumber += phoneNumber.substring(0, 3) + '-';
    formattedPhoneNumber += phoneNumber.substring(3, 6) + '-';
    formattedPhoneNumber += phoneNumber.substring(6, 10);
  } else if (phoneNumber.length >= 7) {
    formattedPhoneNumber += phoneNumber.substring(0, 3) + '-';
    formattedPhoneNumber += phoneNumber.substring(3, 6) + '-';
    formattedPhoneNumber += phoneNumber.substring(6);
  } else if (phoneNumber.length >= 4) {
    formattedPhoneNumber += phoneNumber.substring(0, 3) + '-';
    formattedPhoneNumber += phoneNumber.substring(3);
  } else {
    formattedPhoneNumber = phoneNumber;
  }

  return formattedPhoneNumber;
};

/**
 * Inosio SSN formatter
 * 123456789 -> 123-45-6789
 * @param ssn
 * @returns
 */
export const InosioSsnFormat = (ssn: string) => {
  ssn = ssn.replace(/\D/g, '');

  if (ssn.length === 0) {
    return '';
  }

  let formattedSsn = '';
  if (ssn.length >= 9) {
    formattedSsn += ssn.substring(0, 3) + '-';
    formattedSsn += ssn.substring(3, 5) + '-';
    formattedSsn += ssn.substring(5, 9);
  } else if (ssn.length >= 6) {
    formattedSsn += ssn.substring(0, 3) + '-';
    formattedSsn += ssn.substring(3, 5) + '-';
    formattedSsn += ssn.substring(5);
  } else if (ssn.length >= 4) {
    formattedSsn += ssn.substring(0, 3) + '-';
    formattedSsn += ssn.substring(3);
  } else {
    formattedSsn = ssn;
  }

  return formattedSsn;
};

/**
 * Takes a number and converts the appropriate string in currency format
 * @param minorUnitsValue any real number expressed in minor units e.g 1000 for $10.00
 * @returns string represetation of the dollar amount with formating e.g $10.00
 */
export const formatAsCurrency = (minorUnitsValue: number) => {
  const number = (minorUnitsValue ?? 0) / 100;
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });
  return formatter.format(number);
};

/**
 *
 * @param dollars String representation in minor units. `$1200` or `1200` or `1200.00` or `1,200.00` or `$1,200.45`
 * @returns Minor units as an int. `$1,200.45` would return `120045`
 */
export const majorUnitsStringToMinorUnitsInt = (dollars: string) => {
  //todo add regex test
  if ([undefined, null, ''].includes(dollars)) return null;

  const dollarsSanitized = dollars.replaceAll('$', '').replaceAll(',', '');

  //try to convert to float
  const floatVal = parseFloat(dollarsSanitized);

  if (isNaN(floatVal)) {
    return NaN;
  }

  //multiple and possibly introduce floating point precion errors
  const floatMinor = floatVal * 100;

  //convert it to back to a string with zero notations. This essentially rounds it.
  const stringValMinor = floatMinor.toFixed();

  //parse it back to an int.
  const intValMinor = parseInt(stringValMinor);

  return intValMinor;
};

/**
 *
 * @param minorUnutsValue any real number expressed in minor units e.g 1000 for $10.00
 * @returns A string representation of the number in major units, with the decimal and with no other formatting.
 */
export const minorToMajorUnits = (minorUnutsValue: number) => {
  /*
  Input Scenarios:
  - null - return null
  - one digit in length => 5 returns ".05"
  - two digits in length => 25 returns ".25"
  - three or more digits in length => 125 return "1.25"
  */

  if (minorUnutsValue === null) return null;

  const stringVersion = minorUnutsValue.toFixed(0);

  switch (stringVersion.length) {
    case 1:
      return '.0'.concat(stringVersion);
    case 2:
      return '.'.concat(stringVersion);
    default:
      const firstNChars = stringVersion.substring(0, stringVersion.length - 2);
      const last2Chars = stringVersion.substring(stringVersion.length - 2);
      return firstNChars.concat('.', last2Chars);
  }
};

/**
 *
 * @param string any string including alphabets only
 * @returns string in sentence case. 'INVITED' would return 'Invited'
 */
export const formatStringToSentenceCase = (string: string) => {
  if (!string) return '';

  //format the string to all lowercase
  const lowercased = string.toLocaleLowerCase();

  //get the first character as upper case
  const firstCharacterUpperCased = lowercased.charAt(0).toLocaleUpperCase();

  //join the uppercased first letter and the rest of the string
  const sentenceCased = firstCharacterUpperCased + lowercased.slice(1);

  return sentenceCased;
};

/**
 * Returns the duration between two dates
 * @param startDateString
 * @param endDateString
 * @returns
 */
export const getDurationBetweenTwoDates = (startDateString?: string, endDateString?: string) => {
  const startDate = startDateString ? new Date(startDateString) : new Date();
  const endDate = endDateString ? new Date(endDateString) : new Date();

  const yearDiff = endDate.getFullYear() - startDate.getFullYear();
  const monthDiff = endDate.getMonth() - startDate.getMonth();

  const totalMonths = yearDiff * 12 + monthDiff;

  return convertMonthsToYears(totalMonths);
};

export const formatE164PhoneNumber = (phone: string) => {
  //check if its a valid e164 number
  const matchesRegex = E164PhoneRegex.test(phone);

  //if valid format it
  if (matchesRegex) return parsePhoneNumber(phone).formatInternational();

  //if not valid, just return what they entered
  return phone;
};
